import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus, Send, Trash2, Megaphone, Archive, ArchiveRestore, CheckCircle2,
  Pencil, Copy, BookmarkPlus, LayoutTemplate,
} from "lucide-react";
import { toast } from "sonner";
import { CampaignEditor, TemplatePicker, BLANK_TEMPLATE } from "@/components/CampaignEditor";
import { AudiencePicker } from "@/components/AudiencePicker";

const TYPES = ["newsletter","client update","investor update","event invite","announcement","sales outreach","sponsor outreach","casting/crew call","project update","general campaign"];

const emptyForm = () => ({
  name: "", campaign_type: "newsletter", subject: "",
  from_name: "", from_email: "", reply_to: "",
  content_html: "", content_text: "",
  editor_mode: "visual", use_branding: true, template_id: null,
  recipient_person_ids: [], recipient_company_ids: [],
});

export default function Campaigns() {
  const [list, setList] = useState([]);
  const [people, setPeople] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [templates, setTemplates] = useState({ starters: [], mine: [], merge_tags: [] });
  const [tab, setTab] = useState("draft");
  const [editing, setEditing] = useState(null);   // form object or null
  const [stage, setStage] = useState("template"); // template | edit
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [templateSave, setTemplateSave] = useState(null); // {name, description}

  const load = useCallback(async () => {
    const [c, p, co, t] = await Promise.all([
      api.get("/campaigns"), api.get("/people"), api.get("/companies"), api.get("/campaign-templates"),
    ]);
    setList(c.data); setPeople(p.data); setCompanies(co.data); setTemplates(t.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const archive = async (id, on) => {
    try {
      await api.post(`/campaigns/${id}/${on ? "archive" : "unarchive"}`);
      toast.success(on ? "Campaign archived" : "Campaign restored");
      load();
    } catch { toast.error("Failed"); }
  };

  const shown = list.filter((c) => {
    if (tab === "archived") return c.archived;
    if (tab === "sent") return c.status === "sent" && !c.archived;
    return c.status !== "sent" && !c.archived;
  });
  const counts = {
    draft: list.filter((c) => c.status !== "sent" && !c.archived).length,
    sent: list.filter((c) => c.status === "sent" && !c.archived).length,
    archived: list.filter((c) => c.archived).length,
  };

  const startNew = () => { setEditing(emptyForm()); setStage("template"); };
  const startEdit = (c) => { setEditing({ ...c }); setStage("edit"); };

  const saveCampaign = async () => {
    try {
      if (editing.id) await api.put(`/campaigns/${editing.id}`, editing);
      else await api.post("/campaigns", editing);
      toast.success(editing.id ? "Campaign updated" : "Draft saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
  };

  const saveAsTemplate = async () => {
    const { name, description } = templateSave || {};
    if (!name?.trim()) { toast.error("Give the template a name"); return; }
    try {
      await api.post("/campaign-templates", {
        name: name.trim(), category: editing.campaign_type, subject: editing.subject,
        content_html: editing.content_html, use_branding: editing.use_branding !== false,
        description: description?.trim() || null,
      });
      toast.success("Saved to My templates");
      setTemplateSave(null);
      load();
    } catch { toast.error("Could not save template"); }
  };

  const duplicate = async (id) => {
    try { await api.post(`/campaigns/${id}/duplicate`); toast.success("Campaign duplicated"); load(); }
    catch { toast.error("Failed"); }
  };
  const send = async (id) => {
    const { data } = await api.post(`/campaigns/${id}/send`);
    if (data.stubbed) toast.info(data.message || "Campaign sent (stub)");
    else toast.success(data.message || "Campaign sent");
    load();
  };
  const remove = async (id) => { if (!window.confirm("Delete campaign?")) return; await api.delete(`/campaigns/${id}`); load(); };

  const applyTemplate = (t) => {
    setEditing((f) => ({
      ...f,
      subject: t.subject || f.subject,
      content_html: t.content_html || "",
      campaign_type: t.category || f.campaign_type,
      use_branding: t.use_branding !== false,
      template_id: t.id === "blank" ? null : t.id,
      name: f.name || (t.id === "blank" ? "" : t.name),
    }));
    setStage("edit");
    setLibraryOpen(false);
  };

  const templateActions = {
    onPick: applyTemplate,
    onDeleteMine: async (t) => { await api.delete(`/campaign-templates/${t.id}`); toast.success("Template deleted"); load(); },
    onRenameMine: async (t, name) => { await api.put(`/campaign-templates/${t.id}`, { name }); load(); },
    onDuplicateMine: async (t) => { await api.post(`/campaign-templates/${t.id}/duplicate`); load(); },
  };

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div className="flex flex-wrap items-center gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Campaigns</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Newsletters, updates, invites and crew calls — written visually or in raw HTML, sent through your provider in Settings → Email.
          </p>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" data-testid="open-template-library" onClick={() => setLibraryOpen(true)}>
            <LayoutTemplate className="mr-2 h-4 w-4" /> Templates
          </Button>
          <Button data-testid="add-campaign-btn" onClick={startNew}>
            <Plus className="mr-2 h-4 w-4" /> New campaign
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="campaign-tabs">
          <TabsTrigger value="draft" data-testid="campaign-tab-draft">Drafts ({counts.draft})</TabsTrigger>
          <TabsTrigger value="sent" data-testid="campaign-tab-sent">Sent ({counts.sent})</TabsTrigger>
          <TabsTrigger value="archived" data-testid="campaign-tab-archived">Archived ({counts.archived})</TabsTrigger>
        </TabsList>
      </Tabs>

      {shown.length === 0 ? (
        <Card className="studio-card p-10 text-center">
          <Megaphone className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" strokeWidth={1.5} />
          <div className="text-lg font-medium">
            {tab === "sent" ? "Nothing sent yet" : tab === "archived" ? "Nothing archived" : "No drafts yet"}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">Start from a template — newsletter, announcement, invite, rate card or crew call.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {shown.map((c) => (
            <Card key={c.id} className="studio-card p-5" data-testid={`campaign-card-${c.id}`}>
              <div className="min-w-0">
                <div className="truncate font-medium">{c.name}</div>
                <div className="truncate text-xs text-muted-foreground">Subject: {c.subject}</div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <Badge variant="secondary" className="text-[10px] font-normal">{c.campaign_type}</Badge>
                  {c.status === "sent" ? (
                    <Badge className="inline-flex items-center gap-1 border-transparent bg-[hsl(var(--halo-teal))]/15 text-[10px] font-normal text-[hsl(var(--halo-teal))]" data-testid={`campaign-sent-badge-${c.id}`}>
                      <CheckCircle2 className="h-2.5 w-2.5" /> SENT{c.sent_at ? ` · ${new Date(c.sent_at).toLocaleString()}` : ""}
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-[10px] font-normal capitalize">{c.status}</Badge>
                  )}
                  {c.use_branding === false && <Badge variant="outline" className="text-[10px] font-normal">Raw HTML</Badge>}
                  {c.archived && <Badge variant="outline" className="text-[10px] font-normal" data-testid={`campaign-archived-badge-${c.id}`}>Archived</Badge>}
                </div>
              </div>
              {c.status === "sent" && (
                <div className="mt-4 grid grid-cols-4 gap-2 text-center text-xs">
                  <Stat label="Sent" value={c.stats?.sent || 0} />
                  <Stat label="Opened" value={c.stats?.opened || 0} />
                  <Stat label="Clicked" value={c.stats?.clicked || 0} />
                  <Stat label="Replied" value={c.stats?.replied || 0} />
                </div>
              )}
              <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
                <div className="text-xs text-muted-foreground" data-testid={`campaign-recipients-${c.id}`}>
                  {Object.values(c.audience || {}).some((v) => (Array.isArray(v) ? v.length : v))
                    ? "Targeted by list / role — resolved at send"
                    : `${(c.recipient_person_ids?.length || 0) + (c.recipient_company_ids?.length || 0)} recipients`}
                </div>
                <div className="flex gap-1">
                  {c.status !== "sent" && (
                    <Button data-testid={`edit-campaign-${c.id}`} variant="ghost" size="sm" title="Edit" onClick={() => startEdit(c)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button data-testid={`duplicate-campaign-${c.id}`} variant="ghost" size="sm" title="Duplicate" onClick={() => duplicate(c.id)}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  {c.status !== "sent" && (
                    <Button data-testid={`send-campaign-${c.id}`} variant="outline" size="sm" onClick={() => send(c.id)}>
                      <Send className="mr-1 h-3.5 w-3.5" /> Send
                    </Button>
                  )}
                  {c.archived ? (
                    <Button data-testid={`unarchive-campaign-${c.id}`} variant="ghost" size="sm" onClick={() => archive(c.id, false)} title="Restore"><ArchiveRestore className="h-3.5 w-3.5" /></Button>
                  ) : (
                    <Button data-testid={`archive-campaign-${c.id}`} variant="ghost" size="sm" onClick={() => archive(c.id, true)} title="Archive"><Archive className="h-3.5 w-3.5" /></Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => remove(c.id)} title="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Template library */}
      <Dialog open={libraryOpen} onOpenChange={setLibraryOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Email templates</DialogTitle>
            <DialogDescription>Start from a starter layout or one of your saved templates.</DialogDescription>
          </DialogHeader>
          <TemplatePicker starters={templates.starters} mine={templates.mine} {...templateActions}
                          onPick={(t) => { setEditing(emptyForm()); applyTemplate(t); }} />
        </DialogContent>
      </Dialog>

      {/* Save as template */}
      <Dialog open={Boolean(templateSave)} onOpenChange={(v) => !v && setTemplateSave(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Save as template</DialogTitle>
            <DialogDescription>Reuse this layout and copy on any future campaign.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Template name">
              <Input data-testid="template-save-name" value={templateSave?.name || ""}
                     onChange={(e) => setTemplateSave((s) => ({ ...s, name: e.target.value }))} />
            </Field>
            <Field label="Description (optional)">
              <Input data-testid="template-save-description" value={templateSave?.description || ""}
                     onChange={(e) => setTemplateSave((s) => ({ ...s, description: e.target.value }))} />
            </Field>
          </div>
          <DialogFooter>
            <Button data-testid="template-save-confirm" onClick={saveAsTemplate}>Save template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign editor */}
      <Dialog open={Boolean(editing)} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto">
          {editing && stage === "template" ? (
            <>
              <DialogHeader>
                <DialogTitle>Start from a template</DialogTitle>
                <DialogDescription>Pick a layout — you can change it or edit the HTML at any point.</DialogDescription>
              </DialogHeader>
              <TemplatePicker starters={[BLANK_TEMPLATE, ...templates.starters]} mine={templates.mine} {...templateActions} />
            </>
          ) : editing && (
            <>
              <DialogHeader>
                <DialogTitle>{editing.id ? "Edit campaign" : "New campaign"}</DialogTitle>
                <DialogDescription>
                  Write it visually or in HTML — the plain-text version is generated when it sends.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="Name (internal)">
                    <Input data-testid="campaign-name" value={editing.name}
                           onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                  </Field>
                  <Field label="Type">
                    <Select value={editing.campaign_type} onValueChange={(v) => setEditing({ ...editing, campaign_type: v })}>
                      <SelectTrigger data-testid="campaign-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label="Subject">
                  <Input data-testid="campaign-subject" value={editing.subject}
                         onChange={(e) => setEditing({ ...editing, subject: e.target.value })} />
                </Field>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <Field label="From name">
                    <Input value={editing.from_name || ""} onChange={(e) => setEditing({ ...editing, from_name: e.target.value })} />
                  </Field>
                  <Field label="From email">
                    <Input value={editing.from_email || ""} onChange={(e) => setEditing({ ...editing, from_email: e.target.value })} />
                  </Field>
                  <Field label="Reply-to">
                    <Input value={editing.reply_to || ""} onChange={(e) => setEditing({ ...editing, reply_to: e.target.value })} />
                  </Field>
                </div>

                <CampaignEditor value={editing} mergeTags={templates.merge_tags}
                                onChange={(patch) => setEditing((f) => ({ ...f, ...patch }))}
                                contactId={editing.recipient_person_ids?.[0]} />

                <div className="grid grid-cols-1 gap-4">
                  <AudiencePicker value={editing} people={people} companies={companies}
                                  onChange={(patch) => setEditing((s) => ({ ...s, ...patch }))} />
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" data-testid="campaign-change-template" onClick={() => setStage("template")}>
                  <LayoutTemplate className="mr-2 h-4 w-4" /> Change template
                </Button>
                <Button variant="outline" data-testid="campaign-save-as-template"
                        onClick={() => setTemplateSave({ name: editing.name || "My template", description: "" })}>
                  <BookmarkPlus className="mr-2 h-4 w-4" /> Save as template
                </Button>
                <Button data-testid="campaign-save" onClick={saveCampaign}>
                  {editing.id ? "Save changes" : "Save draft"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>
);

const Stat = ({ label, value }) => (
  <div className="rounded-md border border-border py-2">
    <div className="font-semibold">{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
  </div>
);
